import { 
    Home, 
    Users, 
    Lightbulb, 
    TrendingUp, 
    HandCoins, 
    Handshake, 
    Building, 
    Settings, 
    UserPlus, 
    Calendar 
} from "lucide-react";
import { Box, Typography } from "@mui/material";
import { Sidebar, SidebarSection } from "../../../components/Sidebar";
import logo from "../../../assets/images/brightloop-logo-web.svg";

export const DealSidebar = ({ project, role }: any) => {

    const disabled = (project.viewpoints.length === 0);
    const hasViewpoints = project.viewpoints.length > 0;
    const isDraft = project.status === "draft";
    const isGuest = role === "guest";
    const shareLink = project.project_share_links[0];
    const hasAnalysis = project.iterations?.at(-1)?.analysis_url && project.iterations?.at(-1)?.analysis_url !== "";

    const numOffersToAdvise = project?.viewpoints?.filter((viewpoint: any) => viewpoint?.interest_advisor)?.length ?? 0;
    const numCandidates = project?.viewpoints?.filter((viewpoint: any) => viewpoint?.interest_work)?.length ?? 0;
    const totalProspectiveSales = project?.viewpoints?.filter((viewpoint: any) => viewpoint?.interest_demo || viewpoint?.interest_beta)?.reduce((acc: number, viewpoint: any) => acc + viewpoint?.max_spend, 0) ?? 0;

    const totalPotentialInvestment = project?.viewpoints?.filter((viewpoint: any) => (viewpoint?.interest_investor || viewpoint.advisor?.funded || viewpoint.advisor?.reserved_spot))?.reduce((acc: number, viewpoint: any) => {
        if (viewpoint?.advisor?.funded) return acc + viewpoint?.advisor?.funded_amount;
        if (viewpoint?.advisor?.reserved_spot) return acc + viewpoint?.advisor?.reserved_amount;
        return acc + (viewpoint?.investment_amount_high + viewpoint?.investment_amount_low) / 2;
    }, 0) ?? 0;

    const baseUrl = role === "guest" ? `/guest/${shareLink.share_url}` : `/o/${project.organization.tag}/d/${project.tag}`;

    const tabStatus = {
        executives: isDraft ? false : isGuest ? shareLink.display_executives : true,
        viewpoints: isGuest ? shareLink.display_viewpoints : hasViewpoints,
        analysis: isGuest ? shareLink.display_analysis && hasAnalysis : hasAnalysis,
        prospects: isGuest ? shareLink.display_prospects : hasViewpoints,
        industry_angels: isGuest ? shareLink.display_investors : hasViewpoints,
        advisors: false,
        candidates: false,
        setup: !isGuest,
        collaboration: !isGuest && !isDraft,
        availability: !isGuest && !isDraft
    };

    const sections: SidebarSection[] = [
        {
            visible: true,
            links: [
                {
                    to: `${baseUrl}`,
                    iconComponent: <Home size={16} />,
                    text: "Executives",
                    enabled: tabStatus.executives,
                    visible: true,
                    end: true
                }
            ]
        },
        {
            header: "Insights",
            visible: true,
            links: [
                {
                    to: `${baseUrl}/viewpoints`,
                    iconComponent: <Users size={16} />,
                    text: "Viewpoints",
                    stats: project?.viewpoints.length,
                    enabled: tabStatus.viewpoints,
                    visible: true
                },
                {
                    to: `${baseUrl}/analysis`,
                    iconComponent: <Lightbulb size={16} />,
                    text: "Analysis",
                    enabled: tabStatus.analysis,
                    visible: true
                }
            ]
        },
        {
            header: "Opportunities",
            visible: true,
            links: [
                {
                    to: `${baseUrl}/prospects`,
                    iconComponent: <TrendingUp size={16} />,
                    text: "Prospects",
                    stats: `\$${Intl.NumberFormat('en', { notation: 'compact' }).format(totalProspectiveSales)}`,
                    enabled: tabStatus.prospects,
                    visible: true
                },
                {
                    to: `${baseUrl}/industry-angels`,
                    iconComponent: <HandCoins size={16} />,
                    text: "Angels",
                    stats: `\$${Intl.NumberFormat('en', { notation: 'compact' }).format(totalPotentialInvestment)}`,
                    enabled: tabStatus.industry_angels,
                    visible: true
                },
                {
                    to: `${baseUrl}/advisors`,
                    iconComponent: <Handshake size={16} />,
                    text: "Advisors",
                    stats: numOffersToAdvise,
                    enabled: tabStatus.advisors,
                    visible: true
                },
                {
                    to: `${baseUrl}/candidates`,
                    iconComponent: <Building size={16} />,
                    text: "Candidates",
                    stats: numCandidates,
                    enabled: tabStatus.candidates,
                    visible: true
                }
            ]
        },
        {
            header: "Management",
            visible: !isGuest,
            links: [
                {
                    to: `${baseUrl}/setup`,
                    iconComponent: <Settings size={16} />,
                    text: "Deal Setup",
                    enabled: tabStatus.setup,
                    visible: true
                },
                {
                    to: `${baseUrl}/collaboration`,
                    iconComponent: <UserPlus size={16} />,
                    text: "Team & Guests",
                    enabled: tabStatus.collaboration,
                    visible: true
                },
                {
                    to: `${baseUrl}/availability`,
                    iconComponent: <Calendar size={16} />,
                    text: "Calendar Setup",
                    enabled: tabStatus.availability,
                    visible: project.prospecting_enabled
                }
            ]
        }
    ];

    const bottomRow = isGuest ? (
        <Box className="bottom-row">
            <Box>
                <Typography variant="body2">Powered by</Typography>
                <img className="logo-image brightloop" src={logo.toString()} />
            </Box>
        </Box>
    ) : undefined;

    return (
        <Sidebar sections={sections} bottomRow={bottomRow} />
    );
}
